import { useEffect, useCallback } from 'preact/hooks'
import { dataTracker } from '@/components/snippets/analytics/analytics'

/**
 * Hook to attach DataTracker click tracking to a Preact component
 * @param {Object} config - Configuration for the data tracker
 * @param {string} config.sectionId - Unique identifier for the section
 * @param {string} config.contentType - Type of content being tracked
 * @param {string} config.campaignId - Campaign ID for analytics
 * @param {string} [config.customSelector] - Optional CSS selector for specific elements to track
 * @returns {Function} Ref callback to attach to the target element
 */
export const useDataTracker = ({ sectionId, contentType, campaignId, customSelector }) => {
  // Create section data object
  const sectionData = {
    sectionId,
    contentType,
    campaignId,
    customSelector
  }

  // Callback to handle ref attachment
  const refCallback = useCallback(
    (node) => {
      if (node) {
        // Add section data to window.sectionData if it doesn't exist
        if (!window.sectionData) {
          window.sectionData = []
        }

        // Check if section data already exists
        const existingSection = dataTracker.getSectionDataById(sectionId)

        if (existingSection) {
          // Update existing section data
          dataTracker.replaceSectionDataById(sectionId, existingSection)
        } else {
          // Add new section data
          window.sectionData.push(sectionData)
        }

        // Ensure the element has the correct ID
        node.id = sectionId
      }
    },
    [sectionId, contentType, campaignId, customSelector]
  )

  // Cleanup effect
  useEffect(() => {
    return () => {
      // Remove section data when component unmounts
      if (window.sectionData) {
        window.sectionData = window.sectionData.filter((section) => section.sectionId !== sectionId)
      }
    }
  }, [sectionId])

  return refCallback
}
